import { Injectable, signal } from '@angular/core';
import { EMPTY, forkJoin, from, Observable, of, Subscription } from 'rxjs';
import { catchError, first, map, switchMap } from 'rxjs/operators';

import { Logger } from '@sondermind/datadog-logging';
import { SegmentService, SegmentSchema } from '@sondermind/segment';

import { IAmplitudeFlagState, IUserContext } from '../models/amplitude-flag-state.model';
import { AmplitudeFlagHttpService } from './amplitude-flag-http.service';

@Injectable({
  providedIn: 'root',
})
export class AmplitudeFlagToolService {
  readonly flags = signal<IAmplitudeFlagState[]>([]);
  readonly loading = signal<boolean>(false);
  readonly error = signal<string | null>(null);
  readonly userContext = signal<IUserContext>({ type: 'anonymous', id: '' });

  private subscription?: Subscription;

  constructor(
    private httpService: AmplitudeFlagHttpService,
    private segmentService: SegmentService<SegmentSchema>,
    private logger: Logger
  ) {
    this.subscription = this.initialize$().pipe(first()).subscribe();
  }

  /**
   * Reload flags from server
   */
  reload(): void {
    this.subscription?.unsubscribe();
    this.subscription = this.initialize$().pipe(first()).subscribe();
  }

  /**
   * Initialize: fetch all flags for current user
   */
  private initialize$(): Observable<void> {
    this.loading.set(true);
    this.error.set(null);

    return this.buildUserContext$().pipe(
      switchMap((userContext) => {
        this.userContext.set(userContext);

        // Only pass anonymousId if user is NOT authenticated
        // If authenticated, Angular HTTP interceptors add Bearer token automatically
        const anonymousId = userContext.type === 'anonymous' ? userContext.id : undefined;

        return this.httpService.fetchFlags$(anonymousId);
      }),
      map((response) => {
        // Map backend DTOs to frontend state (camelCase conversion already done in HTTP service)
        const flagStates: IAmplitudeFlagState[] = response.flags.map((dto) => ({
          key: dto.key,
          currentVariant: dto.currentVariant,
        }));

        this.flags.set(flagStates);
        this.loading.set(false);
      }),
      catchError((error) => {
        this.error.set('Failed to load feature flags');
        this.logger.error('AmplitudeFlagTool: Failed to initialize', error);
        this.loading.set(false);
        return EMPTY;
      })
    );
  }

  /**
   * Toggle a flag between ON and OFF
   * ON: Add to 'on' variant → overrides targeting rules to force on
   * OFF: Remove from 'on' variant → falls back to targeting rules
   * Note: Won't force OFF if targeting has 100% rollout
   */
  toggleFlag(flagKey: string): void {
    const flag = this.flags().find((f) => f.key === flagKey);
    if (!flag) return;

    // Get user context for API call
    // Only pass anonymousId if user is NOT authenticated (Bearer token handled by interceptors)
    const userContext = this.userContext();
    const anonymousId = userContext.type === 'anonymous' ? userContext.id : undefined;

    // Toggle based on current evaluation:
    // - Currently 'on' → remove from 'on' list (turn OFF, falls back to targeting)
    // - Currently not 'on' → add to 'on' list (force ON)
    const operation$: Observable<unknown> = flag.currentVariant === 'on'
      ? this.httpService.clearAssignment$(flagKey, anonymousId)
      : this.httpService.assignVariant$(flagKey, 'on', anonymousId);

    const subscription = operation$.subscribe({
      next: () => {
        // Re-fetch all flags after change to get updated evaluations
        this.reload();
      },
      error: (error: Error) => {
        this.logger.error(`Failed to toggle flag ${flagKey}`, error);
        this.error.set(`Failed to update flag: ${error.message}`);
      },
    });
  }

  /**
   * Clear all flag assignments (remove from 'on' inclusion lists)
   */
  clearAllOverrides(): void {
    const allFlags = this.flags();
    if (allFlags.length === 0) return;

    const userContext = this.userContext();
    const anonymousId = userContext.type === 'anonymous' ? userContext.id : undefined;

    // Clear all flag assignments in parallel
    const clearOperations = allFlags.map((f) => this.httpService.clearAssignment$(f.key, anonymousId).pipe(
      catchError((error) => {
        this.logger.warn(`Failed to clear ${f.key}`, error);
        return of(null); // Continue with other clears
      })
    ));

    const subscription = forkJoin(clearOperations).subscribe({
      next: () => {
        // Re-fetch all flags to get updated evaluations
        this.reload();
      },
      error: (error: Error) => {
        this.logger.error('Failed to clear assignments', error);
        this.error.set('Failed to clear some assignments');
      },
    });
  }

  /**
   * Build user context from Segment's user identification
   * Tries userId() first (for authenticated users), falls back to anonymousId()
   */
  private buildUserContext$(): Observable<IUserContext> {
    return from(this.segmentService.userId()).pipe(
      switchMap((userId) => {
        if (userId) {
          return of({
            type: 'authenticated' as const,
            id: userId
          });
        }

        // No user ID, get anonymous ID from segment
        return from(this.segmentService.anonymousId()).pipe(
          map((anonId) => ({
            type: 'anonymous' as const,
            id: anonId || 'unknown'
          }))
        );
      })
    );
  }
}
